// Importar Proveedores V2: columnas, catálogos, validación, estados y textos. Puro (sin Vue ni store) para probarlo con Node.
// Las reglas de formato son las del DTO del backend (providerImportV2.dto.js): lo que aquí es válido, el DTO lo acepta.
import { cleanText, normalize, normalizeNit, formatCount, plural, catalog, tooLong, freeText, parseSheet } from "@/helpers/importV2";

export const BATCH_SIZE = 250; // proveedores por envío: no hay tope por archivo, se guarda por lotes, uno tras otro
export const TEMPLATE_ROWS = 1000; // filas de la plantilla con lista desplegable (una validación por celda: más la volvería pesada)
const EL_SALVADOR = "El Salvador";
const ENTITIES = ["PERSONA NATURAL", "PERSONA JURIDICA", "SOCIEDAD ANÓNIMA", "CORPORACIÓN"]; // como AddProvider.vue
const SALVADOR_ENTITIES = ENTITIES.slice(0, 2); // en El Salvador solo estas dos, como el alta manual
const ENTITY_ALIASES = { natural: ENTITIES[0], juridica: ENTITIES[1], juridico: ENTITIES[1] };
const SIZES = ["PEQUEÑO", "MEDIANO", "GRANDE"];
const EMAIL = /^[^\s@=+-][^\s@]*@[^\s@.]+(?:\.[^\s@.]+)+$/; // la misma del DTO (no inicia con = + - @: fórmula en Excel/CSV)
const PHONE_SEPARATORS = /[\s()+.-]/g;
const ID_SEPARATORS = /[\s-]/g; // NIT y NRC: sin espacios ni guiones
const SALVADOR_ENTITY_MESSAGE = `En El Salvador solo ${SALVADOR_ENTITIES.join(" o ")} (una S.A. o Corporación se registra como ${ENTITIES[1]})`;

// Única fuente de plantilla, lector y tabla. header = encabezado de V1 (un Excel de V1 carga sin editarlo); max = largo máximo;
// required = obligatoria para todos (teléfono o celular y lo de El Salvador dependen de otros datos: ver finalErrors).
export const COLUMNS = [
  { key: "name", header: "Nombre Proveedor", caption: "Nombre*", width: 32, max: 200, required: true, kind: "text" },
  { key: "country", header: "País Proveedor", caption: "País*", width: 18, required: true, kind: "country" },
  { key: "state", header: "Estado o Departamento Proveedor", caption: "Departamento", width: 24, max: 50, kind: "state" },
  { key: "city", header: "Ciudad Proveedor", caption: "Ciudad", width: 24, max: 50, kind: "city" },
  { key: "address", header: "Dirección Proveedor", caption: "Dirección", width: 32, max: 500, kind: "text" },
  { key: "phone", header: "Teléfono Proveedor", caption: "Teléfono", width: 16, max: 50, kind: "phone" },
  { key: "mobile", header: "Celular Proveedor", caption: "Celular", width: 16, max: 50, kind: "phone" },
  { key: "email", header: "Email Proveedor", caption: "Email*", width: 26, max: 50, required: true, kind: "email" },
  { key: "website", header: "Sitio Web Proveedor", caption: "Sitio web", width: 24, max: 50, kind: "text" },
  { key: "contactName", header: "Nombre Contacto Proveedor", caption: "Contacto", width: 26, max: 150, kind: "text" },
  { key: "contactPhone", header: "Teléfono Contacto Proveedor", caption: "Teléfono contacto", width: 18, max: 50, kind: "phone" },
  { key: "contactMobile", header: "Celular Contacto Proveedor", caption: "Celular contacto", width: 18, max: 50, kind: "phone" },
  { key: "contactEmail", header: "Email Contacto Proveedor", caption: "Email contacto", width: 26, max: 50, kind: "email" },
  { key: "legalName", header: "Nombre Legal Proveedor (Razón Social)", caption: "Razón social", width: 32, max: 100, kind: "text" },
  { key: "legalAddress", header: "Dirección Legal Proveedor", caption: "Dirección legal", width: 32, max: 500, kind: "text" },
  { key: "legalEntity", header: "Persona Natural o Jurídica", caption: "Persona", width: 20, kind: "entity" },
  { key: "nrc", header: "NRC Proveedor", caption: "NRC", width: 14, kind: "nrc" },
  { key: "nit", header: "NIT Proveedor", caption: "NIT", width: 18, kind: "nit" },
  { key: "legalSector", header: "Giro del proveedor", caption: "Giro", width: 36, kind: "sector" },
  { key: "taxPayerSize", header: "Categoría contribuyente", caption: "Categoría", width: 18, kind: "size" },
];
// Columnas que Excel no debe convertir en número (se pierden ceros a la izquierda y los números largos se cortan).
export const TEXT_KEYS = COLUMNS.filter(({ kind }) => ["phone", "nit", "nrc"].includes(kind)).map(({ key }) => key);
const CAPTIONS = Object.fromEntries(COLUMNS.map(({ key, caption }) => [key, caption.replace("*", "")]));

// ---------- Catálogos (índices congelados: Vue no los observa) ----------
// Llaves de identidad (NIT, nombre, razón social) normalizadas: de la fila que llega (campos del contrato) o de lo guardado (columnas).
const identityKeys = ({ nit, name, legalName }) => ({ nit: normalizeNit(nit), name: normalize(name), legalName: normalize(legalName) });
const storedKeys = (provider) => identityKeys({ nit: provider.ProviderLegalNIT, name: provider.ProviderName, legalName: provider.ProviderLegalName });

export function buildCatalogs({ countries = [], departments = [], municipalities = [], activities = [], providers = [] }) {
  const countryNames = [...new Set(countries.map((country) => cleanText(country.name)).filter(Boolean))];
  const departmentCatalog = catalog(departments);
  // Existentes por campo, con la prioridad del contrato (NIT, nombre, razón social); con varios gana el menor ID_Provider.
  // stored: la fila completa de cada proveedor por ID; se lleva al formato del contrato al comparar (ver storedPayload).
  const existing = { nit: new Map(), name: new Map(), legalName: new Map() };
  const stored = new Map();
  providers.forEach((provider) => {
    const known = stored.get(provider.ID_Provider);
    if (!known || Number(provider.ID_Branch) < Number(known.ID_Branch)) stored.set(provider.ID_Provider, provider); // mismo ID en dos sucursales: la de menor ID_Branch, como el servidor
    Object.entries(storedKeys(provider)).forEach(([field, key]) => {
      const id = existing[field].get(key);
      if (key && (id === undefined || provider.ID_Provider < id)) existing[field].set(key, provider.ID_Provider);
    });
  });
  return Object.freeze({
    countries: { names: Object.freeze(countryNames), byText: new Map(countryNames.map((name) => [normalize(name), name])) },
    departments: departmentCatalog,
    // El mismo código de municipio se repite entre departamentos: se indexa por departamento (par Departamento + Codigo).
    cities: new Map([...departmentCatalog.byCode.keys()].map((code) => [code, catalog(municipalities.filter((item) => String(item.Departamento) === code))])),
    cityNames: Object.freeze([...new Set(municipalities.map((item) => cleanText(item.Valores)).filter(Boolean))]),
    activities: catalog(activities),
    entities: new Map([...ENTITIES.map((entity) => [normalize(entity), entity]), ...Object.entries(ENTITY_ALIASES)]),
    sizes: new Map([...SIZES.map((size) => [normalize(size), size]), ["pequena", SIZES[0]]]), // "PEQUEÑA" (femenino) también hay guardado: mismo alias que el DTO
    existing,
    stored,
  });
}

const resolveDepartment = (value, ctx) => ctx.departments.byText.get(normalize(value)) || ctx.departments.byCode.get(cleanText(value));
const resolveCity = (department, value, ctx) => {
  const group = department && ctx.cities.get(department.code);
  return group && (group.byText.get(normalize(value)) || group.byCode.get(cleanText(value)));
};
const resolveSector = (value, ctx) => ctx.activities.byText.get(normalize(value)) || ctx.activities.byCode.get(cleanText(value));
export const isSalvador = (row, ctx) => ctx.countries.byText.get(normalize(row.country)) === EL_SALVADOR;

// ---------- Lectura ----------
// Lo que coincide con un catálogo se guarda con su texto canónico; lo que no, se deja tal cual para que el error lo muestre.
function canonicalize(values, ctx) {
  const country = ctx.countries.byText.get(normalize(values.country));
  const department = country === EL_SALVADOR ? resolveDepartment(values.state, ctx) : null;
  const sector = resolveSector(values.legalSector, ctx);
  const found = {
    country,
    state: department && department.text,
    city: (resolveCity(department, values.city, ctx) || {}).text,
    // Limitación conocida: con textos repetidos en CAT019 (hoy 47214 y 47814) el texto elige el primer código; el otro solo se carga
    // escribiendo su código, y por eso un giro cuyo texto se repite no se reemplaza por su texto (volvería siempre al primero).
    legalSector: sector && ctx.activities.byText.get(normalize(sector.text)) === sector ? sector.text : undefined,
    legalEntity: ctx.entities.get(normalize(values.legalEntity)),
    taxPayerSize: ctx.sizes.get(normalize(values.taxPayerSize)),
  };
  Object.keys(found).forEach((key) => { if (!found[key]) delete found[key]; });
  return found;
}

export function parseProviderRows(sheetRows, ctx) {
  return parseSheet(sheetRows, COLUMNS, ["name"]).map(({ excelRow, values }, index) => ({
    _rowId: index + 1,
    excelRow,
    ...values,
    ...canonicalize(values, ctx),
    errors: {},
    serverErrors: {},
    status: "Lista", // Importada/Actualizada > Con errores > Se actualizará/Sin cambios > Lista (la fija evaluateRows)
    existing: null, // { ID_Provider, by } si ya está en el negocio; by "servidor": el servidor dijo que ya estaba igual
    changes: [], // campos que traen algo nuevo (existe) o que actualizó el servidor (saved "updated")
    saved: false, // false | "created" | "updated"
    ID_Provider: null,
  }));
}

// ---------- Validación (formato: misma regla que el DTO del backend; obligatorios: sobre el registro final) ----------
// Primer problema del campo que llega ("" si está bien o vacío: los obligatorios y lo del registro final los pide finalErrors).
// scope: { salvador (el país que queda: el que llega o, si no trae, el guardado), department, entity } de la fila.
function fieldError({ key, max, kind }, row, ctx, { salvador, department, entity }) {
  const value = cleanText(row[key]);
  switch (kind) {
    case "text":
      return freeText(value, max);
    case "email":
      return tooLong(value, max) || (value && !EMAIL.test(value) ? "Correo no válido" : "");
    case "phone": {
      const digits = value.replace(PHONE_SEPARATORS, "");
      return /^\d*$/.test(digits) ? tooLong(digits, max) : "Solo números (se aceptan espacios, paréntesis, + . -)";
    }
    case "country":
      return value && !ctx.countries.byText.has(normalize(value)) ? `«${value}» no está en la lista de países; elija uno` : "";
    case "state":
      if (!salvador) return freeText(value, max);
      return value && !department ? `«${value}» no es un departamento de El Salvador; elija uno de la lista` : "";
    case "city":
      if (!salvador) return freeText(value, max);
      // Sin departamento válido la ciudad no se puede comprobar: el error queda en el departamento.
      if (!value || !department) return "";
      return resolveCity(department, value, ctx) ? "" : `«${value}» no es una ciudad de ${department.text}; elija una de la lista`;
    case "entity":
      if (!value) return "";
      if (!entity) return `Valor no permitido: «${value}»; use ${(salvador ? SALVADOR_ENTITIES : ENTITIES).join(", ")}`;
      return salvador && !SALVADOR_ENTITIES.includes(entity) ? SALVADOR_ENTITY_MESSAGE : "";
    case "nit":
    case "nrc": {
      const digits = value.replace(ID_SEPARATORS, ""); // solo separadores ("-") cuenta como vacío, como en el DTO
      if (!digits) return "";
      if (kind === "nit") return /^(\d{9}|\d{14})$/.test(digits) ? "" : "Debe tener 9 o 14 dígitos (con o sin guiones)";
      return /^\d{1,8}$/.test(digits) ? "" : "Debe tener de 1 a 8 dígitos (con o sin guion)";
    }
    case "sector":
      return value && !resolveSector(value, ctx) ? `«${value}» no está en el catálogo de giros; elija uno de la lista` : "";
    case "size":
      return value && !ctx.sizes.has(normalize(value)) ? `Valor no permitido: «${value}»; use ${SIZES.join(", ")}` : "";
    default:
      return "";
  }
}

// ---------- Proveedor que ya existe: lo que llega contra lo guardado ----------
const STORED_COLUMNS = {
  name: "ProviderName", legalName: "ProviderLegalName", email: "ProviderEmail", contactEmail: "ProviderContactEmail", phone: "ProviderPhone",
  mobile: "ProviderMobileNumber", contactPhone: "ProviderContactPhone", contactMobile: "ProviderContactMobile", website: "ProviderWebsite",
  address: "ProviderAddress", legalAddress: "ProviderLegalAddress", contactName: "ProviderContactName", country: "ProviderCountry",
  state: "ProviderState", city: "ProviderCity", nit: "ProviderLegalNIT", nrc: "ProviderLegalNRC", legalSector: "ProviderLegalSector",
  legalEntity: "ProviderLegalEntity", taxPayerSize: "ProviderLegalTaxPayerSize",
};

const countryOf = (value, ctx) => ctx.countries.byText.get(normalize(value)) || cleanText(value);

// Lo guardado en el formato del contrato (los mismos códigos y dígitos que toPayload). Departamento y ciudad se leen con el país que llega
// en la fila (es el que quedará) y la ciudad en el departamento que llega (si no trae, el guardado). El país en sí es siempre el guardado.
function storedPayload(provider, row, ctx) {
  const values = Object.fromEntries(Object.entries(STORED_COLUMNS).map(([key, column]) => [key, provider[column]]));
  return { ...toPayload({ ...values, country: row.country || values.country }, ctx, row.state || values.state), country: countryOf(values.country, ctx) };
}

// Campos que llegan con valor y difieren de lo guardado. Un vacío conserva lo guardado y no es un cambio. Teléfonos, NIT y NRC se
// comparan por dígitos; lo demás sin mayúsculas, tildes ni espacios repetidos (los catálogos ya vienen en su valor canónico).
const DIGIT_KINDS = ["phone", "nit", "nrc"];
const sameValue = (kind, a, b) => (DIGIT_KINDS.includes(kind) ? a.replace(/\D/g, "") === b.replace(/\D/g, "") : normalize(a) === normalize(b));
const changedFields = (mine, current) => COLUMNS.filter(({ key, kind }) => mine[key] && !sameValue(kind, mine[key], current[key])).map(({ key }) => key);

// Reglas sobre el registro final (lo guardado + lo que llega con valor; un alta: solo lo que llega), las mismas del servicio. Obligatorios solo si
// la fila escribe algo (alta o con cambios): una existente sin cambios queda "Sin cambios" aunque lo guardado esté incompleto. Todos: nombre,
// país, email y teléfono o celular. El Salvador: además persona (natural o jurídica), giro, NIT (o DUI), departamento y ciudad del departamento,
// y NRC si la persona es jurídica. Persona y ciudad también se revisan si esas celdas llegan; el error de la ciudad va en la celda que llegó
// (departamento si solo llegó él, si no ciudad). Un teléfono, NIT o NRC guardado sin ningún dígito ("N/A") cuenta como vacío.
function finalErrors(final, mine, writes, ctx) {
  const salvador = final.country === EL_SALVADOR;
  const has = (key) => (["phone", "mobile", "nit", "nrc"].includes(key) ? /\d/.test(final[key]) : !!final[key]);
  const keys = ["name", "country", "email", ...(salvador ? ["legalEntity", "legalSector", "nit", "state", "city"] : []), ...(salvador && final.legalEntity === ENTITIES[1] ? ["nrc"] : [])];
  const errors = writes ? Object.fromEntries(keys.filter((key) => !has(key)).map((key) => [key, "Campo obligatorio"])) : {};
  if (writes && !has("phone") && !has("mobile")) errors.phone = "Indique teléfono o celular";
  if (salvador && has("legalEntity") && !SALVADOR_ENTITIES.includes(final.legalEntity) && (writes || mine.legalEntity)) errors.legalEntity = SALVADOR_ENTITY_MESSAGE;
  const unknownDepartment = mine.state && !ctx.departments.byCode.has(mine.state); // ese error ya sale en el departamento
  if (salvador && has("state") && has("city") && !unknownDepartment && !cityBelongs(final.state, final.city, ctx) && (writes || mine.state || mine.city)) {
    errors[mine.state && !mine.city ? "state" : "city"] = "La ciudad no pertenece al departamento";
  }
  return errors;
}

const findExisting = (keys, ctx) => {
  const by = ["nit", "name", "legalName"].find((field) => keys[field] && ctx.existing[field].has(keys[field]));
  return by ? { ID_Provider: ctx.existing[by].get(keys[by]), by } : null;
};

// Una fila no puede mezclar dos proveedores: cada otra llave que trae (no la que dio el principal) que no coincide con el mismo campo del principal
// pero sí con OTRO proveedor es un conflicto en su celda (un valor igual al del principal no cuenta aunque otro proveedor lo comparta: nombres
// repetidos en la BD). Devuelve { campo: mensaje }, con los mismos textos que el servidor ("este/esta" y "el/la").
const IDENTITY_LABELS = { nit: ["Este NIT", "el NIT"], name: ["Este nombre", "el nombre"], legalName: ["Esta razón social", "la razón social"] };
function identityConflicts(keys, existing, ctx) {
  const conflicts = {};
  const own = existing && storedKeys(ctx.stored.get(existing.ID_Provider));
  Object.keys(IDENTITY_LABELS).forEach((field) => {
    const other = existing && field !== existing.by && keys[field] && ctx.existing[field].get(keys[field]);
    if (other && other !== existing.ID_Provider && own[field] !== keys[field]) conflicts[field] = `${IDENTITY_LABELS[field][0]} es de otro proveedor (${other}); ${IDENTITY_LABELS[existing.by][1]} es de ${existing.ID_Provider}. Corrija uno de los dos`;
  });
  return conflicts;
}

// Una pasada sobre todas las filas, en orden. Un proveedor que ya existe se valida como uno nuevo (formato de lo que llega y reglas sobre
// el registro final, obligatorios solo si escribe algo) y sin mezclar a otro proveedor (identityConflicts); sin errores queda "Se actualizará"
// (trae algo distinto de lo guardado) o "Sin cambios". Los repetidos del archivo solo se buscan entre filas sin guardar: la primera queda
// limpia y las siguientes se marcan.
export function evaluateRows(rows, ctx) {
  const seen = { nit: new Map(), name: new Map(), legalName: new Map() };
  rows.forEach((row) => {
    if (row.saved) {
      row.errors = {};
      row.status = row.saved === "updated" ? "Actualizada" : "Importada";
      return;
    }
    // Un "sin cambios" que dijo el servidor se respeta hasta que la fila se edite (la lista local puede ir atrasada o compararlo distinto).
    if (row.existing && row.existing.by === "servidor") {
      Object.assign(row, { errors: {}, changes: [], status: "Sin cambios" });
      return;
    }
    const keys = identityKeys(row);
    row.existing = findExisting(keys, ctx);
    const conflicts = identityConflicts(keys, row.existing, ctx);
    const stored = row.existing && ctx.stored.get(row.existing.ID_Provider);
    const mine = toPayload(row, ctx);
    const current = stored ? storedPayload(stored, row, ctx) : null;
    const changes = current ? changedFields(mine, current) : [];
    const final = Object.fromEntries(Object.entries(mine).map(([key, value]) => [key, value || (current ? current[key] : "")]));
    const rules = finalErrors(final, mine, !current || changes.length > 0, ctx);
    const scope = { salvador: final.country === EL_SALVADOR, entity: ctx.entities.get(normalize(row.legalEntity)) };
    scope.department = scope.salvador ? resolveDepartment(row.state, ctx) : null;
    const errors = {};
    COLUMNS.forEach((column) => {
      const message = fieldError(column, row, ctx, scope) || rules[column.key] || conflicts[column.key];
      if (message) errors[column.key] = message;
    });
    Object.keys(seen).forEach((field) => {
      if (!keys[field]) return;
      const first = seen[field].get(keys[field]);
      if (first === undefined) seen[field].set(keys[field], row.excelRow);
      else if (!errors[field]) errors[field] = `Repetido en el archivo: igual que la fila ${first}`;
    });
    row.errors = { ...errors, ...row.serverErrors };
    const failed = Object.keys(row.errors).length > 0;
    row.changes = failed ? [] : changes;
    row.status = failed ? "Con errores" : !row.existing ? "Lista" : row.changes.length ? "Se actualizará" : "Sin cambios";
  });
}

// ---------- Edición en la tabla ----------
// Lista del desplegable de la celda, o null si es texto libre. Departamento y ciudad solo son listas en El Salvador.
export function editorItems(row, field, ctx) {
  const salvador = isSalvador(row, ctx);
  switch (field) {
    case "country": return ctx.countries.names;
    case "state": return salvador ? ctx.departments.names : null;
    case "city": {
      if (!salvador) return null;
      const group = ctx.cities.get((resolveDepartment(row.state, ctx) || {}).code);
      return group ? group.names : [];
    }
    case "legalEntity": return salvador ? SALVADOR_ENTITIES : ENTITIES;
    case "legalSector": return ctx.activities.names;
    case "taxPayerSize": return SIZES;
    default: return null;
  }
}

export const cityBelongs = (state, city, ctx) => !cleanText(city) || !!resolveCity(resolveDepartment(state, ctx), city, ctx);

// Cascada de la ubicación: si cambia el departamento en El Salvador y la ciudad ya no pertenece, se limpia. Devuelve los cambios extra.
export function cascadeLocation(row, changes, ctx) {
  const next = { ...row, ...changes };
  if (!("state" in changes) || changes.state === row.state || !isSalvador(next, ctx)) return {};
  return cityBelongs(next.state, next.city, ctx) ? {} : { city: "" };
}

const BULK_FIELDS = ["country", "state", "city", "legalEntity", "legalSector", "taxPayerSize"];

// Errores de catálogo que se repiten en 2 o más filas (mismo texto; en ciudad, también el mismo departamento): una elección los
// corrige todos. Máximo 5 grupos. Cada grupo trae sus filas, su texto de origen y la lista de valores a elegir.
export function bulkGroups(rows, ctx) {
  const groups = new Map();
  rows.forEach((row) => {
    if (row.saved) return;
    BULK_FIELDS.forEach((field) => {
      const items = row.errors[field] && !row.serverErrors[field] ? editorItems(row, field, ctx) : null;
      if (!items || !items.length) return; // sin lista no hay con qué corregir (ej. ciudad sin departamento válido)
      const source = cleanText(row[field]);
      if (!source && field !== "country") return; // solo el país vacío se corrige en bloque
      const key = `${field}|${field === "city" ? `${normalize(row.state)}|` : ""}${normalize(source)}`;
      const group = groups.get(key) || { key, field, source, items, rows: [] };
      group.rows.push(row);
      groups.set(key, group);
    });
  });
  return [...groups.values()]
    .filter((group) => group.rows.length >= 2)
    .sort((a, b) => b.rows.length - a.rows.length)
    .slice(0, 5)
    .map((group) => ({
      ...group,
      count: group.rows.length,
      items: group.field === "legalEntity" && group.rows.some((row) => isSalvador(row, ctx)) ? SALVADOR_ENTITIES : group.items,
      label: `${CAPTIONS[group.field]} ${group.source ? `«${group.source}»` : "vacío"}${group.field === "city" ? ` (departamento ${group.rows[0].state})` : ""}`,
    }));
}

// Igual que editar la celda a mano: lo elegido reemplaza el valor y descarta el error que vino del servidor para ese campo.
export function applyBulk(group, value) {
  group.rows.forEach((row) => {
    row[group.field] = value;
    const serverErrors = { ...row.serverErrors };
    delete serverErrors[group.field];
    row.serverErrors = serverErrors;
  });
}

// ---------- Guardar ----------
// Cuerpo del contrato: todo texto; en El Salvador departamento y ciudad viajan como código, giro como código, teléfonos y NIT/NRC sin separadores.
// "En El Salvador" es por el país que queda y la ciudad se busca en el departamento que queda: lo que llega o, si la fila no trae, lo del
// proveedor guardado. cityState: el departamento donde buscar la ciudad (lo guardado lo busca en el que llega, si lo trae).
export function toPayload(row, ctx, cityState) {
  const stored = row.existing && ctx.stored.get(row.existing.ID_Provider);
  const salvador = isSalvador({ country: row.country || (stored ? stored.ProviderCountry : "") }, ctx);
  const department = salvador ? resolveDepartment(row.state, ctx) : null;
  const city = salvador ? resolveCity(resolveDepartment(cityState || row.state || (stored ? stored.ProviderState : ""), ctx), row.city, ctx) : null;
  const sector = resolveSector(row.legalSector, ctx);
  const text = (field) => cleanText(row[field]);
  const phone = (field) => text(field).replace(PHONE_SEPARATORS, "");
  return {
    name: text("name"),
    legalName: text("legalName"),
    email: text("email"),
    contactEmail: text("contactEmail"),
    phone: phone("phone"),
    mobile: phone("mobile"),
    contactPhone: phone("contactPhone"),
    contactMobile: phone("contactMobile"),
    website: text("website"),
    address: text("address"),
    legalAddress: text("legalAddress"),
    contactName: text("contactName"),
    country: countryOf(row.country, ctx),
    state: department ? department.code : text("state"),
    city: city ? city.code : text("city"),
    nit: text("nit").replace(ID_SEPARATORS, ""),
    nrc: text("nrc").replace(ID_SEPARATORS, ""),
    legalSector: sector ? sector.code : text("legalSector"),
    legalEntity: ctx.entities.get(normalize(row.legalEntity)) || text("legalEntity"),
    taxPayerSize: ctx.sizes.get(normalize(row.taxPayerSize)) || text("taxPayerSize"),
  };
}

// ---------- Plantilla ----------
export const templateLists = (ctx) => [
  { title: "Países", key: "country", values: ctx.countries.names },
  { title: "Departamentos", key: "state", values: ctx.departments.names },
  { title: "Ciudades", key: "city", values: ctx.cityNames },
  { title: "Giros", key: "legalSector", values: ctx.activities.names },
  { title: "Persona Natural o Jurídica", key: "legalEntity", values: ENTITIES },
  { title: "Categoría contribuyente", key: "taxPayerSize", values: SIZES },
];

export function instructionRows(ctx) {
  const department = ctx.departments.byCode.values().next().value;
  const group = department && ctx.cities.get(department.code);
  const example = {
    name: "Distribuidora Sol", country: EL_SALVADOR, state: department ? department.text : "", city: group && group.names[0] ? group.names[0] : "",
    phone: "2222-3333", email: "ventas@sol.com", legalEntity: ENTITIES[0], legalSector: ctx.activities.names[0] || "", nit: "0614-010101-101-1",
  };
  return [
    ["Instrucciones"],
    [`Llene la hoja Proveedores desde la fila 2. No hay límite de filas: al guardar se envían en lotes de ${BATCH_SIZE} proveedores, uno tras otro. Las listas desplegables están en las primeras ${formatCount(TEMPLATE_ROWS)} filas; en las demás escriba el valor igual que en la hoja Catalogos.`],
    ["Obligatorios para todos (columnas con *): nombre, país y email, y teléfono o celular (al menos uno de los dos). Para un proveedor del extranjero basta con eso."],
    [`Si el país es El Salvador también son obligatorios: persona (natural o jurídica), giro, NIT (o DUI), departamento y ciudad; y el NRC si la persona es jurídica.`],
    ["País, persona, giro y categoría: use los nombres de la hoja Catalogos. Si algo no coincide lo corrige en pantalla."],
    [`En El Salvador departamento y ciudad deben ser del catálogo (la ciudad del departamento elegido) y la persona solo puede ser ${SALVADOR_ENTITIES.join(" o ")}: una S.A. o Corporación se registra como ${ENTITIES[1]}. En otro país son texto libre.`],
    ["NIT o DUI: 9 o 14 dígitos, con o sin guiones. NRC: de 1 a 8 dígitos. Teléfonos: solo números (se aceptan espacios, paréntesis, + . -)."],
    ["Un proveedor que ya existe (mismo NIT, nombre o razón social) se actualiza solo con los campos que traen valor y son distintos de lo guardado: una celda vacía no borra nada. Si no hay nada nuevo se muestra «Sin cambios» y no se piden obligatorios (no se escribe nada). Los obligatorios se piden sobre el resultado final de lo que se crea o se cambia, así que lo ya guardado cuenta."],
    ["Los proveedores nuevos quedan disponibles en todas las sucursales."],
    [],
    ["Ejemplo (no lo copie a la hoja Proveedores):"],
    COLUMNS.map((column) => column.header),
    COLUMNS.map((column) => example[column.key] || ""),
  ];
}

// ---------- Textos ----------
export const rowErrorText = (row, separator = "\n") => Object.entries(row.errors || {}).map(([field, message]) => `${CAPTIONS[field] || field}: ${message}`).join(separator);
const fieldsText = (fields) => (fields || []).map((field) => CAPTIONS[field] || field).join(", ");

export const canSend = (row) => row.status === "Lista" || row.status === "Se actualizará"; // lo que se selecciona y se envía
export const isOpen = (row) => !row.saved && row.status !== "Sin cambios"; // trabajo sin guardar: salir lo perdería

// Texto visible de la columna Detalle: qué se creó o actualizó, qué se hará, o qué falla y cómo corregirlo.
export function rowDetail(row) {
  const id = row.ID_Provider || (row.existing && row.existing.ID_Provider);
  const fields = fieldsText(row.changes);
  if (row.saved === "updated") return `Actualizado ${id}${fields ? `: ${fields}` : ""}`;
  if (row.saved) return `Creado como ${id}`;
  if (row.status === "Se actualizará") return `Se actualizará ${id}: ${fields}`;
  if (row.status === "Sin cambios") return `Sin cambios: ya está igual (${id})`;
  return rowErrorText(row, " · ");
}

// Un solo mensaje sobre lo que falta por guardar. counts: { errors, unchanged, ready (marcadas y válidas), readyUpdates (de esas, las que
// actualizan un proveedor que ya existe), open (sin guardar y con algo que hacer) }.
export function statusBanner({ errors, unchanged, ready, readyUpdates, open }, hasResult) {
  const note = unchanged ? ` ${plural(unchanged, "fila sin cambios no se enviará", "filas sin cambios no se enviarán")}.` : "";
  if (errors) {
    const lead = ready ? `${plural(ready, "lista para guardar", "listas para guardar")}. ` : "";
    return { key: "errors", tone: "danger", title: plural(errors, "fila con errores", "filas con errores"), detail: `${lead}Las celdas en rojo se corrigen aquí mismo, sin volver al Excel.${note}` };
  }
  if (!open) return unchanged && !hasResult ? { key: "done", tone: "info", title: "Todos los proveedores del archivo ya están igual", detail: "No hay nada que guardar: ninguno trae datos nuevos." } : null;
  if (!ready) return { key: "none", tone: "warning", title: "No hay filas seleccionadas y válidas.", detail: `Marque en la tabla las filas que quiere guardar.${note}` };
  return { key: "ready", tone: "success", title: plural(ready, "proveedor listo para guardar", "proveedores listos para guardar"), detail: `Presione Guardar: ${plural(ready - readyUpdates, "nuevo", "nuevos")} y ${formatCount(readyUpdates)} por actualizar.${note}` };
}

// Resultado acumulado de todos los lotes guardados.
export function resultBanner({ created, updated, unchanged }) {
  const title = `${plural(created, "creado", "creados")}, ${plural(updated, "actualizado", "actualizados")}, ${formatCount(unchanged)} sin cambios`;
  return { key: "result", tone: created + updated ? "success" : "info", title, detail: created + updated ? "Con su ID de proveedor." : "Ningún proveedor cambió: ya estaban igual." };
}
